import { PublishedFormId } from '@furnish-hx/constants/FormId';
import FormTypeId from '@core/constants/FormTypeId';
import { TrayShapeId } from '@core/constants/Shape';
import { FieldDimensionId } from '@core/constants/FieldDimension';
import fieldPortfolioData from './common/fieldsPortfolioData';
import {
    IFormFieldKey,
    IForm,
    IFormInputType,
    IDimensionUnit,
} from '../../interfaces/Form';
import additionalDataFactory from './common/fieldsAdditionalData';
import fieldsContact from './common/fieldsContact';
import {
    fieldsWineCellarRoom,
    fieldsWineCellarSquareMeter,
    fieldsWineCellarDegrees,
    fieldsWineCellarHumidity,
    fieldsWineCellarShelf,
    fieldsShelfMaterial,
    fieldsWineCellarOption,
    fieldsWineNbBootle,
    fieldsWineCellarHeightCeilingDimension,
    fieldsWineCellarMaterial,
    fieldsWineCellarMultiSelector,
} from './common/fieldsWineCellar';

const WineCellarForm: IForm = {
    formId       : PublishedFormId.WineCellarDedicatedRoom,
    type         : FormTypeId.WineCellar,
    thumbnailURL : '/resized-images/forms/wineCellarDedicatedRoom.jpg',
    fieldsOrder  : [
        IFormFieldKey.PortfolioData,
        IFormFieldKey.Comment,
        IFormFieldKey.WineCellarRoom,
        IFormFieldKey.Dimension,
        IFormFieldKey.WineCellarSquareMeter,
        IFormFieldKey.WineCellarDegrees,
        IFormFieldKey.WineCellarHumidity,
        IFormFieldKey.WineCellarLightingExpose,
        IFormFieldKey.ShapeDimension,
        IFormFieldKey.MultiSelector,
        IFormFieldKey.WineCellarMaterial,
        IFormFieldKey.WineCellarShelf,
        IFormFieldKey.WineCellarShelfMaterial,
        IFormFieldKey.WineCellarNbBottle,
        IFormFieldKey.AddOns,
        IFormFieldKey.AdditionalData,
        IFormFieldKey.Contact,
    ],
    fields: {
        portfolioData : fieldPortfolioData,
        comment       : {
            comment: {
                value    : undefined,
                required : false,
            },
        },
        wineCellarRoom           : fieldsWineCellarRoom,
        dimension                : fieldsWineCellarHeightCeilingDimension,
        wineCellarSquareMeter    : fieldsWineCellarSquareMeter,
        wineCellarDegrees        : fieldsWineCellarDegrees,
        wineCellarHumidity       : fieldsWineCellarHumidity,
        wineCellarLightingExpose : { value: undefined },
        multiSelector            : fieldsWineCellarMultiSelector,
        shapeDimension           : {
            shape: {
                value   : undefined,
                options : [
                    TrayShapeId.SingleShape,
                    TrayShapeId.LShape,
                    TrayShapeId.UShape,
                ],
                required: true,
            },
            dimension: {
                options: {
                    [TrayShapeId.SingleShape]: {
                        [FieldDimensionId.Width]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1000, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Depth]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 200, max: 2000 },
                            required     : true,
                            defaultValue : 400,
                        },
                        [FieldDimensionId.Height]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 900, max: 4000 },
                            required     : true,
                            defaultValue : 1500,
                        },
                    },
                    [TrayShapeId.LShape]: {
                        [FieldDimensionId.Width]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1000, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Depth]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 200, max: 2000 },
                            required     : true,
                            defaultValue : 400,
                        },
                        [FieldDimensionId.Height]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 900, max: 4000 },
                            required     : true,
                            defaultValue : 1500,
                        },
                        [FieldDimensionId.Width2]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1000, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Depth2]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 200, max: 2000 },
                            required     : true,
                            defaultValue : 400,
                        },
                        [FieldDimensionId.Height2]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 900, max: 4000 },
                            required     : true,
                            defaultValue : 1500,
                        },
                    },
                    [TrayShapeId.UShape]: {
                        [FieldDimensionId.Width]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1000, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Depth]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 200, max: 2000 },
                            required     : true,
                            defaultValue : 400,
                        },
                        [FieldDimensionId.Height]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 900, max: 4000 },
                            required     : true,
                            defaultValue : 1500,
                        },
                        [FieldDimensionId.Width2]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1000, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Depth2]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 200, max: 2000 },
                            required     : true,
                            defaultValue : 400,
                        },
                        [FieldDimensionId.Height2]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 900, max: 4000 },
                            required     : true,
                            defaultValue : 1500,
                        },
                        [FieldDimensionId.Width3]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1000, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Depth3]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 200, max: 2000 },
                            required     : true,
                            defaultValue : 400,
                        },
                        [FieldDimensionId.Height3]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 900, max: 4000 },
                            required     : true,
                            defaultValue : 1500,
                        },
                    },
                },
            },
        },
        wineCellarMaterial      : fieldsWineCellarMaterial,
        wineCellarShelf         : fieldsWineCellarShelf,
        wineCellarShelfMaterial : fieldsShelfMaterial,
        wineCellarNbBottle      : fieldsWineNbBootle,
        addOns                  : fieldsWineCellarOption,
        additionalData          : additionalDataFactory({
            budgetMin          : 2000,
            budgetMax          : 25000,
            budgetDefaultValue : 6000,
            budgetStep         : 500,
            hasQuantity        : false,
        }),
        contact: fieldsContact,
    },
};

export default WineCellarForm;
